<div class="hero-gallery">
  <!-- MOBILE (stacked layout, <=900px): one full-width image, the others reached
       by swiping (native scroll-snap). A "1 / 3" counter and dots show there is
       more; tapping a slide opens the full-screen gallery at that slide. -->
  <div class="hg-mobile" *ngIf="slides.length">
    <div class="hg-track" #track (scroll)="onTrackScroll()">
      <div
        class="hg-slide"
        *ngFor="let s of slides; let i = index; trackBy: trackSlide"
        [class.is-plate]="s.kind === 'plate'"
        [class.is-loading]="s.kind !== 'plate' && !s.src"
        (click)="openLightboxAt(i)"
      >
        <img *ngIf="s.src" [src]="s.src" [alt]="s.alt" draggable="false" />
        <!-- The vehicle images are Plate Viewer renders, not the car the plate
             is registered to: say so on the image itself. -->
        <div class="hg-slide-plate" *ngIf="s.kind === 'plate' && !s.src">
          <pla-plate
            [rawPlate]="registration"
            [white]="false"
            [mobileSmall]="false"
            [openModal]="false"
            [showPrice]="false"
            [useCustom]="true"
            [customSize]="9"
            [customFontSize]="92"
          ></pla-plate>
        </div>
      </div>
    </div>
    <span class="hg-count" *ngIf="slides.length > 1" aria-hidden="true"
      >{{ slideIndex + 1 }} / {{ slides.length }}</span
    >
    <div class="hg-dots" *ngIf="slides.length > 1">
      <button
        type="button"
        class="hg-dot"
        *ngFor="let s of slides; let i = index; trackBy: trackSlide"
        [class.is-active]="i === slideIndex"
        [attr.aria-label]="'Show image ' + (i + 1) + ' of ' + slides.length"
        (click)="goTo(i)"
      ></button>
    </div>
  </div>

  <!-- DESKTOP: main image + two thumbnails. -->
  <div class="hg-desktop">
  <!-- LARGE MAIN IMAGE — shows whichever of the three images is selected.
       Clicking it opens that image enlarged in a lightbox. -->
  <div
    class="hg-main"
    [class.is-plate]="showPlateInMain"
    [class.is-loading]="mainLoading"
    (click)="openLightbox()"
  >
    <img
      *ngIf="!showPlateInMain && mainSrc"
      [src]="mainSrc"
      [alt]="registration + ' on an illustrative vehicle (PlateX visualisation)'"
    />
    <div class="hg-main-plate" *ngIf="showPlateInMain">
      <pla-plate
        [rawPlate]="registration"
        [white]="false"
        [mobileSmall]="false"
        [openModal]="false"
        [showPrice]="false"
        [useCustom]="true"
        [customSize]="9"
        [customFontSize]="92"
      ></pla-plate>
    </div>
  </div>

  <!-- THUMBNAIL ROW — two columns, always the two images NOT in the main slot.
       Clicking a thumbnail promotes it to main; the previous main drops here. -->
  <div class="hg-row">
    <!-- Rendered plate on a white card (existing PlateX render). Placed first so
         it sits on the LEFT of the thumbnail row. -->
    <div class="hg-tile hg-plate" *ngIf="view !== 'plate'" (click)="showPlate()">
      <pla-plate
        [rawPlate]="registration"
        [white]="false"
        [mobileSmall]="false"
        [openModal]="false"
        [showPrice]="false"
        [useCustom]="true"
        [customSize]="9"
        [customFontSize]="63"
      ></pla-plate>
    </div>

    <!-- Zoom tile (detail-tile, fill 0.70). Promotes detail-main (0.48). Placed
         on the RIGHT, holding the zoomed on-vehicle crop. -->
    <div
      class="hg-tile hg-zoom"
      *ngIf="view !== 'detail' && hasZoom"
      (click)="showZoom()"
    >
      <img [src]="detailTileSrc" [alt]="registration + ' plate detail'" />
    </div>

    <!-- Full vehicle render (cover-cropped to tile aspect). Only appears in the
         row when another image is promoted to main; otherwise it is the main. -->
    <div
      class="hg-tile hg-full"
      *ngIf="view !== 'full' && fullSrc"
      (click)="showFull()"
    >
      <img [src]="fullSrc" [alt]="registration + ' on an illustrative vehicle (PlateX visualisation)'" />
    </div>
  </div>
  </div>

  <!-- LIGHTBOX — full-screen gallery of all images, opened at the tapped /
       selected one. Swipe (or arrows / arrow keys) between images; tapping the
       backdrop, the close button or Esc dismisses it. -->
  <div
    class="hg-lightbox"
    *ngIf="lightboxOpen"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="registration + ' image gallery'"
    (click)="closeLightbox()"
  >
    <div class="hg-lb-track" #lbTrack (scroll)="onLbScroll()">
      <div class="hg-lb-slide" *ngFor="let s of slides; trackBy: trackSlide">
        <img
          *ngIf="s.src"
          class="hg-lb-img"
          [src]="s.src"
          [alt]="s.alt"
          draggable="false"
          (click)="$event.stopPropagation()"
        />
        <div
          class="hg-lb-plate"
          *ngIf="s.kind === 'plate' && !s.src"
          (click)="$event.stopPropagation()"
        >
          <pla-plate
            [rawPlate]="registration"
            [white]="false"
            [mobileSmall]="false"
            [openModal]="false"
            [showPrice]="false"
            [useCustom]="true"
            [customSize]="9"
            [customFontSize]="140"
          ></pla-plate>
        </div>
      </div>
    </div>

    <button class="hg-lb-close" type="button" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
    <ng-container *ngIf="slides.length > 1">
      <button
        class="hg-lb-nav hg-lb-prev"
        type="button"
        aria-label="Previous image"
        [disabled]="lbIndex === 0"
        (click)="$event.stopPropagation(); lbGo(lbIndex - 1)"
      >
        <mat-icon>chevron_left</mat-icon>
      </button>
      <button
        class="hg-lb-nav hg-lb-next"
        type="button"
        aria-label="Next image"
        [disabled]="lbIndex === slides.length - 1"
        (click)="$event.stopPropagation(); lbGo(lbIndex + 1)"
      >
        <mat-icon>chevron_right</mat-icon>
      </button>
      <span class="hg-lb-count">{{ lbIndex + 1 }} / {{ slides.length }}</span>
    </ng-container>
  </div>
</div>
